import React from 'react';
import { C, LAYOUT } from '../../shared/design-tokens.js';

function Tile({ title, children, accent }: { title: string; children: React.ReactNode; accent?: string }) {
  return (
    <div style={LAYOUT.subPanel}>
      <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: accent || C.gold, marginBottom: 10 }}>
        {title}
      </div>
      <div style={{ color: C.text, fontSize: 13, lineHeight: 1.6 }}>
        {children}
      </div>
    </div>
  );
}

function ReadinessRow({ label, state, detail }: { label: string; state: 'present' | 'absent'; detail?: string }) {
  const colour = state === 'present' ? C.green : C.textMuted;
  const marker = state === 'present' ? '●' : '○';
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '14px 1fr', gap: 10, alignItems: 'baseline', padding: '6px 0' }}>
      <div style={{ color: colour, fontSize: 14, lineHeight: 1 }}>{marker}</div>
      <div>
        <div style={{ color: C.text, fontSize: 13 }}>{label}</div>
        {detail && <div style={{ color: C.textMuted, fontSize: 11, marginTop: 2 }}>{detail}</div>}
      </div>
    </div>
  );
}

export default function TrainingModule() {
  return (
    <div style={LAYOUT.subPanel}>
      <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.textMuted, marginBottom: 8 }}>
        Training module
      </div>
      <p style={{ marginTop: 0, lineHeight: 1.6 }}>
        Training V2 is a placeholder seam for Suture LMS integration. No live
        course, learner, or completion data flows through this portal yet — this
        tab is intentionally empty of functionality until the LMS is wired in.
      </p>

      <Tile title="Purpose">
        This tab will become the portal-side home for Suture LMS when that
        integration lands. Until then it stays a text-only seam so nothing on
        this screen can be mistaken for live training state.
      </Tile>

      <Tile title="What training tracking exists today">
        <p style={{ margin: 0 }}>
          The onboarding pipeline already tracks two real training timestamps on
          each new-hire candidate:{' '}
          <code style={{ color: C.gold }}>leads_and_hires.training_started_at</code>{' '}
          and{' '}
          <code style={{ color: C.gold }}>leads_and_hires.training_completed_at</code>.
          The activation gate in <code style={{ color: C.gold }}>PATCH /api/onboarding/:id/status</code>{' '}
          refuses to activate a new hire until{' '}
          <code style={{ color: C.gold }}>training_completed_at</code> is set.
        </p>
        <p style={{ marginTop: 10, marginBottom: 0, color: C.textMuted, fontSize: 12 }}>
          That flow lives on the Onboarding side of the portal and is not
          duplicated here. This tab will not surface or aggregate those
          timestamps — doing so would blur the Training/Onboarding boundary
          before a real LMS exists.
        </p>
      </Tile>

      <Tile title="Integration readiness">
        <div style={{ display: 'grid', gap: 2 }}>
          <ReadinessRow
            label="LMS backend endpoints"
            state="absent"
            detail="No /api/training, /api/lms, /api/suture, or /api/courses routes in workers/api/src/index.js"
          />
          <ReadinessRow
            label="Course / learner / completion tables"
            state="absent"
            detail="No LMS-related tables in supabase/migrations/"
          />
          <ReadinessRow
            label="Suture API credentials wiring"
            state="absent"
            detail="No worker code references SUTURE_* or LMS_* env vars — the integration has not been coded against any credential shape yet"
          />
          <ReadinessRow
            label="Onboarding training timestamps"
            state="present"
            detail="leads_and_hires.training_started_at / training_completed_at — surfaced on the Onboarding tab, not here"
          />
          <ReadinessRow
            label="Suture integration contract"
            state="absent"
            detail="Integration shape TBD — scope/ownership/data model not defined in the repo yet"
          />
        </div>
        <p style={{ marginTop: 12, marginBottom: 0, color: C.textMuted, fontSize: 11 }}>
          Every line above reflects a direct inspection of the current repo.
          None of it is aspirational — absent means the artifact genuinely does
          not exist today.
        </p>
      </Tile>
    </div>
  );
}
